import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  CheckCircle,
  Email,
  Login,
  Rocket,
  Group,
  Dashboard,
} from '@mui/icons-material';
import api from '../../services/api';

const steps = [
  'Payment Processed',
  'Studio Created',
  'Email Verification Sent',
];

export const StudioSignupSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [studioName, setStudioName] = useState<string>('');
  const [adminEmail, setAdminEmail] = useState<string>('');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      completeSignup();
    } else {
      setError('No session ID found. Please contact support.');
      setIsLoading(false);
    }
  }, [sessionId]);

  const completeSignup = async () => {
    try {
      const response = await api.post('/studio-signup/complete', {
        sessionId: sessionId,
      });
      
      setStudioName(response.data.studio?.name || 'Your Studio');
      setAdminEmail(response.data.adminEmail || '');
      setIsLoading(false);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to complete signup. Please contact support.');
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0A0E1A',
        }}
      >
        <Card sx={{ 
          p: 4,
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px)',
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Completing your studio setup...</Typography>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0E1A',
        backgroundImage: `
          radial-gradient(ellipse at top right, rgba(76, 175, 80, 0.1) 0%, transparent 50%),
          radial-gradient(ellipse at bottom left, rgba(255, 107, 53, 0.1) 0%, transparent 50%)
        `,
        padding: 2,
      }}
    >
      <Card sx={{ 
        maxWidth: 700, 
        width: '100%',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <CardContent sx={{ p: 4 }}>
          {error ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={() => navigate('/studio-signup')}
                  sx={{ mr: 2 }}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  mb: 3,
                  padding: 3,
                  borderRadius: '50%',
                  background: 'rgba(76, 175, 80, 0.1)',
                  border: '2px solid rgba(76, 175, 80, 0.3)',
                }}>
                  <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
                </Box>
                
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                  Welcome to Abydos Studio Hub!
                </Typography>
                
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {studioName} has been created successfully
                </Typography>
              </Box>

              <Stepper activeStep={3} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label} completed={true}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Alert severity="info" icon={<Email />} sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Check your email!</strong>
                </Typography>
                <Typography variant="body2">
                  We've sent a verification email to <strong>{adminEmail}</strong>. 
                  Please click the link in the email to activate your admin account.
                </Typography>
              </Alert>

              <Card sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rocket sx={{ mr: 1 }} />
                  What's next?
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Email color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Verify your email"
                      secondary="Click the verification link we sent to activate your account"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <Login color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Sign in to your studio"
                      secondary="Use your admin credentials to access your studio dashboard"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <Group color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Invite your team"
                      secondary="Add engineers and collaborators to your studio"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <Dashboard color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Start collaborating"
                      secondary="Create projects and begin working with your team"
                    />
                  </ListItem>
                </List>
              </Card>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  startIcon={<Login />}
                  sx={{
                    background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                    boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 24px rgba(255, 107, 53, 0.4)',
                    },
                  }}
                >
                  Go to Login
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.open('https://docs.abydosone.com', '_blank')}
                >
                  View Documentation
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};